import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AdminPanel } from "@/components/site/Admin";
import { IllustrationSlot } from "@/components/site/Illustrations";
import { DonationWidget } from "@/components/site/DonationWidget";
import { OtherWays } from "@/components/site/OtherWays";
import { useSite, ReloginNeeded } from "@/context/SiteContext";
import { errorMessage } from "@/lib/api";
import { money, parseAmount, percent } from "@/lib/format";
import { useInView, prefersReducedMotion } from "@/lib/useInView";

const ROWS = [
  { y: 304, hw: 112, n: 9 }, { y: 268, hw: 92, n: 7 }, { y: 236, hw: 90, n: 7 },
  { y: 190, hw: 72, n: 6 }, { y: 156, hw: 54, n: 5 }, { y: 118, hw: 38, n: 4 }, { y: 86, hw: 22, n: 3 },
];
const COLORS = ["#F5AE3D", "#FFE7A8", "#F7C98A"];
const sag = (t) => 7 * (1 - t * t);
const LIGHTS = ROWS.flatMap((r, ri) => Array.from({ length: r.n }, (_, i) => {
  const t = (2 * i) / (r.n - 1) - 1;
  return { x: 150 + t * r.hw, y: r.y + sag(t), c: COLORS[(i + ri) % 3] };
}));
const GARLANDS = ROWS.map((r) => `M${150 - r.hw - 6} ${r.y - 1} Q150 ${r.y + 16} ${150 + r.hw + 6} ${r.y - 1}`);

const tier = (t, b, hw, n) => {
  let d = `M150 ${t} C ${150 - hw * 0.35} ${t + (b - t) * 0.35}, ${150 - hw * 0.8} ${b - 18}, ${150 - hw} ${b - 6}`;
  const seg = (2 * hw) / n;
  for (let i = 0; i < n; i += 1) d += ` Q ${150 - hw + seg * i + seg / 2} ${b + 12}, ${150 - hw + seg * (i + 1)} ${b - 6}`;
  return `${d} C ${150 + hw * 0.8} ${b - 18}, ${150 + hw * 0.35} ${t + (b - t) * 0.35}, 150 ${t} Z`;
};
const TIERS = [[190, 322, 136, 5, "#24453D"], [140, 274, 118, 5, "#2A4F45"], [92, 210, 92, 4, "#24453D"], [48, 150, 64, 3, "#2E574C"]];

function useSequencedLights(target, active) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (prefersReducedMotion()) { setShown(target); return undefined; }
    const id = setInterval(() => setShown((n) => {
      if (n === target) { clearInterval(id); return n; }
      return n < target ? n + 1 : target;
    }), 120);
    return () => clearInterval(id);
  }, [target, active]);
  return shown;
}

function TreeSvg({ pct }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const target = Math.round((Math.min(pct, 100) / 100) * LIGHTS.length);
  const shown = useSequencedLights(target, inView);
  const [pulse, setPulse] = useState({ i: -1, n: 0 });
  const full = pct >= 100 && shown === LIGHTS.length;

  const nearest = (e) => {
    const svg = ref.current;
    const m = svg.getScreenCTM();
    if (!m) return;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    let best = -1, bd = 24 * 24;
    LIGHTS.forEach((l, i) => { const d = (l.x - p.x) ** 2 + (l.y - p.y) ** 2; if (d < bd) { bd = d; best = i; } });
    if (best >= 0 && best !== pulse.i) setPulse({ i: best, n: pulse.n + 1 });
  };

  return (
    <svg ref={ref} viewBox="0 0 300 360" className="h-auto w-full max-w-[420px] touch-manipulation" aria-hidden="true" data-testid="tree-svg"
      data-lit={target} data-shown={shown} onPointerMove={nearest} onPointerDown={nearest}>
      <defs>
        <radialGradient id="of-halo"><stop offset="0" stopColor="#FFD98A" stopOpacity="0.95" /><stop offset="1" stopColor="#F5AE3D" stopOpacity="0" /></radialGradient>
      </defs>
      <ellipse cx="150" cy="348" rx="110" ry="9" fill="#24453D" opacity="0.12" />
      <rect x="134" y="316" width="32" height="32" rx="7" fill="#7A5236" />
      {TIERS.map(([t, b, hw, n, fill]) => <path key={t} d={tier(t, b, hw, n)} fill={fill} />)}
      {GARLANDS.map((d) => <path key={d} d={d} stroke="#FFF3D6" strokeOpacity="0.35" strokeWidth="1.4" fill="none" />)}
      {LIGHTS.map((l, i) => {
        const on = i < shown;
        return (
          <g key={`${i}-${pulse.i === i ? pulse.n : 0}`} className={`of-light ${on ? "lit" : ""} ${pulse.i === i ? "pulse" : ""}`} data-testid={on ? "tree-bulb-lit" : "tree-bulb-unlit"}>
            <circle className="of-light-halo" cx={l.x} cy={l.y} r="12" fill="url(#of-halo)" />
            <circle className="of-light-core" cx={l.x} cy={l.y} r="5" fill={on ? l.c : "#1B3530"} stroke={on ? "none" : "#4F7A6C"} strokeWidth="1" />
          </g>
        );
      })}
      <g transform="translate(150 40)">
        <circle className={`of-star-glow ${full ? "on" : ""}`} r="22" fill="url(#of-halo)" />
        <polygon points="0,-16 4.7,-5 16,-5 7,2 10.5,14 0,7 -10.5,14 -7,2 -16,-5 -4.7,-5"
          fill={full ? "#F5AE3D" : "#FFFCF8"} stroke="#C98A1E" strokeWidth="1.5" data-testid={full ? "tree-star-lit" : "tree-star-unlit"} />
      </g>
    </svg>
  );
}

function TreeAdmin({ settings }) {
  const { saveSettings } = useSite();
  const [raised, setRaised] = useState(String(settings.raised ?? 0));
  const [goal, setGoal] = useState(String(settings.goal ?? 10000));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const r = raised.trim() === "0" ? 0 : parseAmount(raised);
    const g = parseAmount(goal);
    if (r === null) return setError("Please enter the raised total as a number, like 2500.");
    if (!g) return setError("Please enter a goal of $1 or more.");
    setBusy(true);
    setError("");
    try {
      await saveSettings({ raised: r, goal: g });
      toast.success("Saved. The tree is updated for everyone.");
    } catch (err) {
      if (!(err instanceof ReloginNeeded)) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminPanel title="Update the tree" testId="tree-admin-panel">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="admin-raised" className="of-label">Raised so far ($)</label>
          <input id="admin-raised" inputMode="decimal" className="of-input" value={raised} onChange={(e) => setRaised(e.target.value)} data-testid="admin-inline-edit-raised" />
        </div>
        <div>
          <label htmlFor="admin-goal" className="of-label">Goal ($)</label>
          <input id="admin-goal" inputMode="decimal" className="of-input" value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="admin-inline-edit-goal" />
        </div>
      </div>
      {error && <p className="of-error" role="alert" data-testid="tree-admin-error">{error}</p>}
      <button type="button" onClick={save} disabled={busy} className="of-btn-soft mt-4 w-full sm:w-auto" data-testid="tree-admin-save-button">
        {busy ? "Saving…" : "Save tree"}
      </button>
    </AdminPanel>
  );
}

export function ChristmasTree() {
  const { settings } = useSite();
  const raised = settings?.raised ?? 0;
  const goal = settings?.goal ?? 10000;
  const pct = percent(raised, goal);
  const text = useMemo(() => `${money(raised)} raised of ${money(goal)} · ${pct}%`, [raised, goal, pct]);

  return (
    <section id="give" aria-labelledby="tree-heading" className="of-container py-12 sm:py-16" data-testid="tree-section">
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-12">
        <div className="of-card overflow-hidden bg-[linear-gradient(180deg,#FFF3F6_0%,#FFFCF8_70%)] p-6 sm:p-10 lg:sticky lg:top-24">
          <p className="of-eyebrow">Our goal this year</p>
          <h2 id="tree-heading" className="mt-3 text-3xl font-semibold sm:text-4xl">Every gift lights the tree.</h2>
          <p className="mt-3 text-lg text-[#3B4A44]">Each light on the tree is a little closer to a toy in a child's hands.</p>
          <IllustrationSlot asset="TREE" className="mt-6 flex justify-center" testId="tree-illustration">
            {settings ? <TreeSvg pct={pct} /> : <div className="aspect-[5/6] w-full max-w-[420px]" />}
          </IllustrationSlot>
          <p className="sr-only" data-testid="tree-sr-text">{`Tree is ${Math.min(pct, 100)}% lit: ${money(raised)} raised of ${money(goal)}`}</p>
          <p className="mt-4 text-center font-display text-2xl font-semibold text-[#24453D] sm:text-3xl" data-testid="progress-tracker-text">
            {settings ? text : " "}
          </p>
          {settings && <TreeAdmin key={`${raised}-${goal}`} settings={settings} />}
        </div>
        <div data-testid="give-section">
          <p className="of-eyebrow">Give money</p>
          <h2 className="mb-4 mt-2 text-2xl font-semibold sm:text-3xl">Bring the holidays to a child in treatment.</h2>
          <DonationWidget returnPath="/" />
        </div>
      </div>
      <OtherWays />
    </section>
  );
}
